import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { createEmbeddingContext } from "amazon-quicksight-embedding-sdk";
import { DashboardService } from "../../services/admin/dashboard.service";
import { FrameOptions } from "amazon-quicksight-embedding-sdk";
import { QuicksightService } from "../../services/user/quicksight.service";
import { CloudWatchRumService } from "../../services/cloudwatch-rum.service";
import { CorrelationService } from "../../services/correlation.service";
import { ReportExportFormat, ReportExportParameter, ReportExportService, ReportExportStatus } from "../../services/user/report-export.service";
import { ScheduleSubscriptionComponent } from "../../schedule-subscription/schedule-subscription.component";
import { Content } from "../../services/admin/content.service";
import { environment } from "../../../../environments/environment";
import { overflowIconConfig } from "../../../constants";
import { firstValueFrom } from "rxjs";

/** One choice in the Export menu: the item shape the AWLDS f-overflow-menu reads, plus the format it exports. */
interface ExportMenuItem {
  name: string;
  uuid: string;
  selected: boolean;
  disabled: boolean;
  srText: string;
  format: ReportExportFormat;
}

const EXPORT_ITEMS: ExportMenuItem[] = [
  { name: "CSV", uuid: "export-csv", selected: false, disabled: false, srText: "Export as CSV", format: "CSV" },
  { name: "Excel", uuid: "export-excel", selected: false, disabled: false, srText: "Export as Excel", format: "EXCEL" },
];
// The same choices greyed out. Both lists are built once, so the menu isn't handed a new list on every change check.
const EXPORT_ITEMS_WAITING: ExportMenuItem[] = EXPORT_ITEMS.map(item => ({ ...item, disabled: true }));

@Component({
  selector: "app-embedded-dashboard",
  standalone: true,
  imports: [CommonModule, ScheduleSubscriptionComponent],
  templateUrl: "./embedded-dashboard.component.html",
  styleUrls: ["./embedded-dashboard.css"],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EmbeddedDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly EXPORT_TIMEOUT_MS = 10 * 60 * 1000;
  private isViewInitialized = false;
  private frameMountStartTime: number | null = null;
  private contentLoadStartTime: number | null = null;
  private lastFilterEventTime: number | null = null;
  private lastSheetChangeEventTime: number | null = null;
  private pendingSheetRenderStartTime: number | null = null;
  private pendingSheetRenderSheetId: string | null = null;
  private pendingSheetRenderTimeoutId: any = null;
  private correlationId: string = '';
  private hasReportedContentLoaded = false;
  private readonly isInternalRoute = (window.location.pathname || '').toLowerCase().startsWith('/internal');
  private readonly internalReportsBaseUrl = '/internal/reports-and-dashboards';
  private readonly internalFavoritesBaseUrl = '/internal/favorited-reports-dashboards';
  debugInfo: string | null = null;

  dashboardId: string = "";
  sheetId?: string = "";
  embedUrl: string = "";
  contentTitle: string = "";
  contentType: string = "";
  contentId: string = "";
  quickSightContentId: string = "";
  visualId: string = "";
  description: string = "";
  descriptionObj: object = {};

  // Export the report as CSV or Excel with the current selections (ARAP-1889); shown only where the export API exists.
  readonly exportEnabled = environment.reportExportEnabled === true;
  readonly exportIconConfig = overflowIconConfig;
  exportInProgress = false;
  exportMessage = "";
  private embeddedExperience: any = null;
  private exportPollTimer: any = null;
  private destroyed = false;

  // Subscribe with the selections on screen (ARAP-1888); shown only where reportSubscribeEnabled is on.
  readonly subscribeEnabled = environment.reportSubscribeEnabled === true;
  subscribeItem: Content | null = null;
  selectionsFromReport: ReportExportParameter[] = [];
  // The report's own sheet from its content tag; sheetId follows the tab the user is on.
  private reportSheetId = "";

  items = [
    { name: "Home", url: this.isInternalRoute ? this.internalReportsBaseUrl : "/" },
    { name: "Reports and Dashboards", url: this.isInternalRoute ? this.internalReportsBaseUrl : "/reports-and-dashboards" },
    { name: "", url: "../" },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dashboardService: DashboardService,
    private location: Location,
    private quicksightService: QuicksightService,
    private cloudWatchRumService: CloudWatchRumService,
    private correlationService: CorrelationService,
    private reportExportService: ReportExportService,
  ) {}

  ngOnInit(): void {
    // Get dashboard ID from route parameters
    this.dashboardId = this.route.snapshot.paramMap.get("dashboardId") || "";

    // Start end-to-end content load timer and resolve correlation/context for this report session
    this.contentLoadStartTime = performance.now();
    this.contentId = this.route.snapshot.queryParamMap.get("contentId") || "";
    this.visualId = this.route.snapshot.queryParamMap.get("visualId") || "";
    this.correlationId = this.route.snapshot.queryParamMap.get("correlationId") || this.correlationService.get() || this.correlationService.create();

    // Share correlationId with the auth interceptor so API calls carry the same ID
    this.correlationService.set(this.correlationId);

    this.cloudWatchRumService.recordPageView(`/embedded-dashboard/${this.dashboardId}`);
    
    // Get any query parameters that might override backend values
    const queryTitle = this.route.snapshot.queryParamMap.get("title");

    this.route.queryParams.subscribe((params) => {
      const isPinnedOrigin = params['origin'] === 'pinned';

      if (this.isInternalRoute) {
        this.items[0].url = this.internalReportsBaseUrl;
        this.items[1].name = 'Reports and Dashboards';
        this.items[1].url = this.internalReportsBaseUrl;

        if (isPinnedOrigin) {
          this.items[0].url = this.internalFavoritesBaseUrl;
          this.items[1].name = 'Pinned Reports and Dashboards';
          this.items[1].url = this.internalFavoritesBaseUrl;
        }
      }

      // Update breadcrumb for pinned content
      if (isPinnedOrigin && !this.isInternalRoute) {
        this.items[1].name = `Pinned ${this.items[1].name}`;
        this.items[1].url = '';
      }
      
      // The title will be set from backend response, but can be overridden by query params
      if (queryTitle) {
        this.contentTitle = queryTitle;
        this.updateBreadcrumbTitle();
      }
    });

    this.getEmbeddedUrl();
    
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    if (this.embedUrl) {
      this.embedDashboard();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.exportPollTimer) {
      clearTimeout(this.exportPollTimer);
    }
  }

  /** The Export menu works once the report has loaded, one export at a time. */
  get canExport(): boolean {
    return this.exportEnabled && !!this.embeddedExperience && this.hasReportedContentLoaded && !this.exportInProgress;
  }

  /** CSV and Excel, greyed out until the report has loaded and while an export runs. */
  get exportItems(): ExportMenuItem[] {
    return this.canExport ? EXPORT_ITEMS : EXPORT_ITEMS_WAITING;
  }

  /** Starts the export for the format picked in the Export menu. */
  onExportSelected(event: Event): void {
    const uuid = (event as CustomEvent<{ uuid?: string }>).detail?.uuid;
    const format = EXPORT_ITEMS.find(item => item.uuid === uuid)?.format;
    if (format) {
      void this.exportReport(format);
    }
  }

  /** Subscribe is offered where the scheduled run can export it: a dashboard, or a report whose table is known. */
  get showSubscribe(): boolean {
    return this.subscribeEnabled && (String(this.contentType).toUpperCase() === "DASHBOARD" || !!this.visualId);
  }

  /** Subscribe works once the report has loaded. */
  get canSubscribe(): boolean {
    return this.showSubscribe && !!this.embeddedExperience && this.hasReportedContentLoaded;
  }

  /** Opens the subscription form with the selections on screen filled in; the report stays loaded underneath. */
  async subscribeFromReport(): Promise<void> {
    if (!this.canSubscribe) {
      return;
    }
    let selections: ReportExportParameter[] = [];
    try {
      selections = (await this.embeddedExperience.getParameters()) || [];
    } catch (error) {
      console.warn("Could not read the report's selections; the subscription form opens without them.", error);
    }
    // The client always comes from the signed-in user, never from the page.
    this.selectionsFromReport = selections.filter(selection => String(selection?.Name).toLowerCase() !== "pclientid");
    this.subscribeItem = {
      dashboardId: this.dashboardId,
      contentId: this.contentId,
      title: this.contentTitle,
      contentType: this.contentType || "Report",
      sheetId: this.reportSheetId,
      visualId: this.visualId,
    } as Content;
    this.cloudWatchRumService.addCustomEvent("subscribe_from_report_opened", {
      ...this.reportContext(),
      selectionCount: this.selectionsFromReport.length,
    });
  }

  /** Back to the report once the subscription is saved or the form is left. */
  closeSubscribe(): void {
    this.subscribeItem = null;
    this.selectionsFromReport = [];
  }

  /**
   * Exports the report's table with the selections the user has on screen right now. The backend runs the
   * export as the user's own QuickSight identity, so the file holds only rows they are allowed to see.
   */
  async exportReport(format: ReportExportFormat): Promise<void> {
    if (!this.canExport) {
      return;
    }
    const label = format === "EXCEL" ? "Excel" : "CSV";
    const startedAt = performance.now();
    this.exportInProgress = true;
    this.exportMessage = `Preparing your ${label} file...`;
    try {
      const parameters = (await this.embeddedExperience.getParameters()) || [];
      const sheetId = (await this.embeddedExperience.getSelectedSheetId?.()) || this.sheetId || undefined;
      const started = await firstValueFrom(this.reportExportService.startExport({
        dashboardId: this.dashboardId,
        contentId: this.contentId || undefined,
        sheetId,
        format,
        parameters,
      }));
      this.cloudWatchRumService.addCustomEvent("report_export_requested", { ...this.reportContext(), format });
      const finished = await this.waitForExport(started, label, startedAt);
      const exportDurationMs = Math.round(performance.now() - startedAt);
      if (finished.status === "COMPLETED" && finished.downloadUrl) {
        this.triggerDownload(finished.downloadUrl);
        this.exportMessage = `Your ${label} file is downloading.`;
        this.cloudWatchRumService.addCustomEvent("report_export_completed", { ...this.reportContext(), format, exportDurationMs });
      } else {
        this.exportMessage = finished.message || "The export didn't finish. Try again in a minute.";
        this.cloudWatchRumService.addCustomEvent("report_export_failed", {
          ...this.reportContext(), format, exportDurationMs, errorCode: finished.errorCode || "FAILED",
        });
      }
    } catch (error: any) {
      if (this.destroyed) {
        return;
      }
      this.exportMessage = error?.error?.message || "The export couldn't be started. Try again in a minute.";
      this.cloudWatchRumService.addCustomEvent("report_export_failed", {
        ...this.reportContext(),
        format,
        exportDurationMs: Math.round(performance.now() - startedAt),
        errorCode: error?.error?.errorCode || (error?.status ? `HTTP_${error.status}` : "FAILED"),
      });
    } finally {
      this.exportInProgress = false;
    }
  }

  private async waitForExport(started: ReportExportStatus, label: string, startedAt: number): Promise<ReportExportStatus> {
    const pollMs = (started.pollAfterSeconds || 5) * 1000;
    let current = started;
    while (current.status === "RUNNING") {
      if (performance.now() - startedAt > EmbeddedDashboardComponent.EXPORT_TIMEOUT_MS) {
        return { ...current, status: "FAILED", errorCode: "TIMEOUT", message: "The export is taking longer than expected. Try again later." };
      }
      await this.delay(pollMs);
      if (this.destroyed) {
        throw new Error("The page was closed during the export.");
      }
      this.exportMessage = `Preparing your ${label} file... ${Math.round((performance.now() - startedAt) / 1000)} s`;
      current = await firstValueFrom(this.reportExportService.getExport(started.exportId));
    }
    return current;
  }

  /** Opens the short-lived download link; the file is sent as an attachment, so the page stays where it is. */
  protected triggerDownload(url: string): void {
    const link = document.createElement("a");
    link.href = url;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  protected delay(ms: number): Promise<void> {
    return new Promise(resolve => {
      this.exportPollTimer = setTimeout(resolve, ms);
    });
  }

  onBreadcrumbSelected(event: Event) {
    const payload = (
      event as CustomEvent<{
        name: string;
        url: string;
      }>
    ).detail;

    if (payload.url === '' || payload.url === '../') {
      this.location.back();
      return;
    }

    this.router.navigateByUrl(payload.url);
  }

  updateBreadcrumbTitle() {
    this.items[2].name = this.contentTitle;
  }

  getEmbeddedUrl() {
    this.dashboardService.getEmbeddedUrl(this.dashboardId, this.contentId).subscribe({
      next: (response: { embed_url: string; dashboard_id?: string; content_id?: string; content_type?: string; title?: string; sheet_id?: string }) => {
        this.embedUrl = response.embed_url;
        
        // Set values from backend response
        this.quickSightContentId = response.content_id || this.quickSightContentId;
        this.contentId = this.contentId || response.content_id || "";
        if (response.content_type) {
          this.contentType = response.content_type;
        }
        if (response.title && !this.contentTitle) {
          this.contentTitle = response.title;
          this.updateBreadcrumbTitle();
        }
        const rawSheetId = response.sheet_id;
        this.reportSheetId = rawSheetId || "";
        this.sheetId = (!rawSheetId || rawSheetId.trim().toLowerCase() === 'null') ? undefined : rawSheetId;
        
        if (this.isViewInitialized) {
          this.embedDashboard();
        }
      },
      error: (error: any) => {
        console.error("Error fetching embedded URL:", error);
        this.recordReportRenderFailed('embed_url_fetch_failed', error);
      },
    });
  }

  async embedDashboard() {
    if (!this.embedUrl) {
      console.error("embedUrl is not available.");
      return;
    }

    // Ensure we have the latest tokens before embedding
    this.quicksightService.refreshTokens();
    
    const frameOptions: FrameOptions = {
      url: this.embedUrl,
      container: "#dashboard-container",
      height: "100%",
      width: "100%",
      className: "embedded-dashboard-frame",
      onChange: (changeEvent: { eventName: string }) => {
        switch (changeEvent.eventName) {
          case "FRAME_MOUNTED":
            this.frameMountStartTime = performance.now();
            window.scrollTo(0, 0);
            break;
          case "FRAME_LOADED":
            // Calculate embedding duration if we have a start time
            if (this.frameMountStartTime !== null) {
              const frameLoadEndTime = performance.now();
              const embeddingDuration = frameLoadEndTime - this.frameMountStartTime;
              
              // Record CloudWatch RUM custom event for successful embedding
              this.cloudWatchRumService.addCustomEvent('report_embedding_loaded', {
                ...this.reportContext(),
                contentTitle: this.contentTitle,
                embeddingDurationMs: embeddingDuration,
              });
              
              // Reset the start time
              this.frameMountStartTime = null;
            }
            break;
        }
      },
    };

    // Define a basic content options object
    const contentOptions = {
      locale: "en-US",
      toolbarOptions: {
        export: this.contentType === 'Dashboard',
        undoRedo: false,
        reset: true,
      },
      sheetOptions: {
        singleSheet: !!this.sheetId?.trim() && this.sheetId?.trim().toLowerCase() !== "null",
        // Opt-in so SIZE_CHANGED fires on sheet change, used as a best-effort render-complete signal
        emitSizeChangedEventOnSheetChange: true,
      },
      parameterPassingMethod: 'URL'
    };

    try {
      // Ensure the user is registered with QuickSight
      await firstValueFrom(this.quicksightService.ensureRegistered());
      
      // Get client ID with retry logic to ensure we have a valid value
      let clientId = await this.getClientIdWithRetry();
      
      if (!clientId) {
        console.warn("Could not obtain a valid client ID after multiple attempts. Proceeding with original options.");
        this.embedWithOriginalOptions(frameOptions, contentOptions);
        return;
      }
      
      console.log("Successfully obtained client ID:", clientId);
      
      // Add client ID to URL parameters if available
      if (frameOptions.url) {
        const url = new URL(frameOptions.url);
        url.searchParams.append('clientId', clientId);
        frameOptions.url = url.toString();
      }

      const enhancedContentOptions = JSON.parse(JSON.stringify(contentOptions))

      // iterate through all properties on this.descriptionObj and take all non null / non empty string values and add those properties and values to a parameters property on enhancedContenOptions obj
      if (!enhancedContentOptions.parameters) {
        enhancedContentOptions.parameters = [];
      }
      
      for (const [key, value] of Object.entries(this.descriptionObj)) {
        if (value !== null && value !== undefined && value !== '') {
          enhancedContentOptions.parameters.push({
            Name: key,
            Values: value,
          });
        }
      }
      
      // Add the pClientId parameter to the content options
      const updatedContentOptions = await firstValueFrom(
        this.quicksightService.addClientIdToEmbedConfig(enhancedContentOptions)
      );

      console.log('enhancedContentOptions', enhancedContentOptions);
      console.log('updatedContentOptions', updatedContentOptions);
      
      // Verify the content options include the client ID
      if (!this.verifyClientIdInOptions(updatedContentOptions, clientId)) {
        console.warn("Client ID not properly added to content options. Using original embedding method.");
        this.embedWithOriginalOptions(frameOptions, enhancedContentOptions);
        return;
      }
      
      // Embed the dashboard and attach SDK event listeners
      const embeddingContext: any = await createEmbeddingContext();
      const embeddedDashboard = await embeddingContext.embedDashboard(frameOptions, updatedContentOptions);
      this.attachSdkEventListeners(embeddedDashboard);
    } catch (error) {
      console.error('Error during dashboard embedding or registration:', error);
      this.recordReportRenderFailed('embedding_failed', error);
      // If registration fails, still try to embed with original options
      this.embedWithOriginalOptions(frameOptions, contentOptions);
    }
  }

  /**
   * Fallback method to embed the dashboard with the original options
   * Used when there's an error adding the client ID
   */
  private async embedWithOriginalOptions(frameOptions: FrameOptions, contentOptions: any) {
    try {
      console.log("Embedding dashboard with original options");
      const embeddingContext: any = await createEmbeddingContext();
      const embeddedDashboard = await embeddingContext.embedDashboard(frameOptions, contentOptions);
      this.attachSdkEventListeners(embeddedDashboard);
    } catch (error) {
      console.error('Error during fallback dashboard embedding:', error);
      this.recordReportRenderFailed('fallback_embedding_failed', error);
    }
  }

  /**
   * Attaches QuickSight Embedding SDK event listeners to capture iframe interactions
   * and publish them as CloudWatch RUM custom events from the parent page.
   */
  private attachSdkEventListeners(embeddedDashboard: any): void {
    if (!embeddedDashboard) return;
    this.embeddedExperience = embeddedDashboard;

    embeddedDashboard.addEventListener('CONTENT_LOADED', (payload: any) => {
      const now = performance.now();
      const contentLoadDurationMs = this.contentLoadStartTime !== null
        ? Math.round(now - this.contentLoadStartTime)
        : null;
      this.cloudWatchRumService.addCustomEvent('report_content_loaded', {
        ...this.reportContext(),
        contentLoadDurationMs,
      });
      this.hasReportedContentLoaded = true;
      // Baseline so the first filter/sheet change measures time since the report became usable
      this.lastFilterEventTime = now;
      this.lastSheetChangeEventTime = now;
    });

    embeddedDashboard.addEventListener('PARAMETERS_CHANGED', (payload: any) => {
      const now = performance.now();
      // Interaction interval only: time since the last filter/report-ready milestone, not render time (see SIZE_CHANGED note below)
      const filterActionIntervalMs = this.lastFilterEventTime !== null
        ? Math.round(now - this.lastFilterEventTime)
        : null;
      this.cloudWatchRumService.addCustomEvent('report_filter_applied', {
        ...this.reportContext(),
        actionSource: this.hasReportedContentLoaded ? 'USER_INTERACTION' : 'INITIAL_PARAMETERS',
        changedParameters: payload?.message?.changedParameters ?? payload?.changedParameters ?? [],
        filterActionIntervalMs,
      });
      this.lastFilterEventTime = now;
    });

    embeddedDashboard.addEventListener('SELECTED_SHEET_CHANGED', (payload: any) => {
      const now = performance.now();
      const newSheetId = payload?.message?.selectedSheet?.SheetId ?? payload?.selectedSheet?.SheetId ?? null;
      // Interaction interval only: time since the last sheet-change/report-ready milestone, not render time
      const sheetActionIntervalMs = this.lastSheetChangeEventTime !== null
        ? Math.round(now - this.lastSheetChangeEventTime)
        : null;
      this.cloudWatchRumService.addCustomEvent('report_sheet_changed', {
        ...this.reportContext(),
        previousSheetId: this.sheetId || null,
        currentSheetId: newSheetId,
        sheetActionIntervalMs,
      });
      this.lastSheetChangeEventTime = now;
      if (newSheetId) {
        this.sheetId = newSheetId;
      }
      this.startSheetRenderMeasurement(newSheetId);
    });

    // SIZE_CHANGED is the only SDK signal close to "visuals finished rendering"; used as a best-effort approximation
    embeddedDashboard.addEventListener('SIZE_CHANGED', (_payload: any) => {
      this.completeSheetRenderMeasurement(true);
    });

    embeddedDashboard.addEventListener('ERROR_OCCURRED', (payload: any) => {
      this.cloudWatchRumService.addCustomEvent('report_error_occurred', {
        ...this.reportContext(),
        errorCode: payload?.message?.errorCode ?? payload?.errorCode ?? 'UNKNOWN',
      });
    });

    // MODAL_OPENED fires when the user opens any modal inside the report (e.g. export/download dialog)
    embeddedDashboard.addEventListener('MODAL_OPENED', (_payload: any) => {
      this.cloudWatchRumService.addCustomEvent('report_modal_opened', {
        ...this.reportContext(),
      });
    });

    embeddedDashboard.addEventListener('CALLBACK_OPERATION_INVOKED', (payload: any) => {
      this.recordQuickSightCustomizationAction(payload);
    });
  }

  /**
   * Starts a bounded wait for a SIZE_CHANGED event after a sheet change, as a best-effort
   * approximation of render completion. QuickSight's embedding SDK (v2.11.3) does not expose
   * a dedicated "render complete"/query-finished event, so this is the closest available signal.
   */
  private startSheetRenderMeasurement(sheetId: string | null): void {
    if (this.pendingSheetRenderTimeoutId) {
      clearTimeout(this.pendingSheetRenderTimeoutId);
    }
    this.pendingSheetRenderStartTime = performance.now();
    this.pendingSheetRenderSheetId = sheetId;
    this.pendingSheetRenderTimeoutId = setTimeout(() => this.completeSheetRenderMeasurement(false), 8000);
  }

  private completeSheetRenderMeasurement(sizeChangedObserved: boolean): void {
    if (this.pendingSheetRenderStartTime === null) {
      return;
    }
    const sheetRenderApproxMs = sizeChangedObserved
      ? Math.round(performance.now() - this.pendingSheetRenderStartTime)
      : null;
    this.cloudWatchRumService.addCustomEvent('report_sheet_render_measured', {
      ...this.reportContext(),
      sheetId: this.pendingSheetRenderSheetId,
      sheetRenderApproxMs,
      sheetRenderMeasured: sizeChangedObserved,
      sheetRenderMeasurement: 'size_changed_event',
    });
    if (this.pendingSheetRenderTimeoutId) {
      clearTimeout(this.pendingSheetRenderTimeoutId);
    }
    this.pendingSheetRenderStartTime = null;
    this.pendingSheetRenderSheetId = null;
    this.pendingSheetRenderTimeoutId = null;
  }

  private recordQuickSightCustomizationAction(payload: any): void {
    const message = payload?.message || payload || {};
    const customActionId = message.CustomActionId || message.customActionId || null;
    const action = this.resolveQuickSightCustomizationAction(customActionId);

    this.cloudWatchRumService.addCustomEvent('quicksight_customization_action', {
      ...this.reportContext(),
      dashboardId: message.DashboardId || this.dashboardId || null,
      sheetId: message.SheetId || this.sheetId || null,
      visualId: message.VisualId || this.visualId || null,
      action,
      customActionId,
      sdkEventName: 'CALLBACK_OPERATION_INVOKED',
      sdkSupported: action !== 'unsupported',
      sdkLimitation: action === 'unsupported' ? 'QuickSight embedding SDK exposes callback operations only for configured custom actions. Native hide/replace/remove-field and sort toolbar gestures are not exposed as typed dashboard message events.' : null,
    });
  }

  private resolveQuickSightCustomizationAction(customActionId: string | null): string {
    const normalizedActionId = (customActionId || '').toLowerCase();

    if (normalizedActionId.includes('reset')) {
      return 'reset';
    }

    if (normalizedActionId.includes('sort')) {
      return 'sort';
    }

    if (normalizedActionId.includes('hide')) {
      return 'hide_field';
    }

    if (normalizedActionId.includes('replace')) {
      return 'replace_field';
    }

    if (normalizedActionId.includes('remove')) {
      return 'remove_field';
    }

    return 'unsupported';
  }

  private reportContext(): Record<string, string | null> {
    const contentId = this.contentId || null;
    return {
      dashboardId: this.dashboardId || null,
      contentId,
      applicationContentId: contentId,
      quickSightContentId: this.quickSightContentId || null,
      visualId: this.visualId || null,
      contentType: this.contentType || null,
      sheetId: this.sheetId || null,
      correlationId: this.correlationId,
    };
  }

  private recordReportRenderFailed(errorType: string, error: any): void {
    this.cloudWatchRumService.addCustomEvent('report_render_failed', {
      ...this.reportContext(),
      errorType,
      errorCode: error?.error?.code || error?.code || null,
      errorMessage: error?.error?.message || error?.message || String(error || 'Unknown error'),
    });
  }

  /**
   * Attempts to get a valid client ID with retry logic
   * @returns A Promise that resolves to the client ID or null if not available after retries
   */
  private async getClientIdWithRetry(maxRetries = 3, delayMs = 500): Promise<string | null> {
    let retries = 0;
    
    while (retries < maxRetries) {
      // Try to get the client ID
      const clientId = this.quicksightService.getClientCode();
      
      if (clientId) {
        return clientId; // Success!
      }
      
      console.log(`Attempt ${retries + 1}/${maxRetries}: Client ID not available yet. Retrying in ${delayMs}ms...`);
      
      // Wait before trying again
      await new Promise(resolve => setTimeout(resolve, delayMs));
      
      // Force a token refresh on subsequent attempts
      if (retries > 0) {
        this.quicksightService.refreshTokens();
      }
      
      retries++;
    }
    
    console.warn(`Failed to get client ID after ${maxRetries} attempts.`);
    return null;
  }
  
  /**
   * Verifies that the client ID is properly included in the content options
   * @param options The content options to verify
   * @param clientId The expected client ID
   * @returns true if the client ID is properly included, false otherwise
   */
  private verifyClientIdInOptions(options: any, clientId: string): boolean {
    if (!options || !options.parameters) {
      return false;
    }
    
    // Look for the pClientId parameter
    const clientIdParam = options.parameters.find((param: any) => param.Name === 'pClientId');
    
    if (!clientIdParam || !clientIdParam.Values || !clientIdParam.Values.length) {
      return false;
    }
    
    // Check that the value matches our client ID
    return clientIdParam.Values[0] === clientId;
  }
}
